import { useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { PageWrapper } from "@/components/layout/PageWrapper";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, RefreshCw, ArrowLeft, AlertTriangle, ChevronRight } from "lucide-react";
import {
  fetchSqlSyncErrorSummary,
  fetchSqlSyncErrorDetails,
  SqlSyncErrorSummaryResponse,
  SqlSyncErrorDetailsResponse,
  SqlSyncErrorGroup,
} from "@/lib/api";
import { ViewGuard } from "@/components/auth/PermissionGuards";

const formatDate = (dateStr: string) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleString();
};

const truncateError = (error: string, maxLen: number = 80) => {
  if (!error) return "-";
  return error.length > maxLen ? error.substring(0, maxLen) + "..." : error;
};

export default function SqlSyncErrorsPage() {
  const [, setLocation] = useLocation();
  const [selectedError, setSelectedError] = useState<{
    entityType: string;
    errorPattern: string;
  } | null>(null);

  const {
    data: summaryData,
    isLoading: summaryLoading,
    isError: summaryError,
    error: summaryErrorObj,
    refetch: refetchSummary,
    isFetching: summaryFetching,
  } = useQuery<SqlSyncErrorSummaryResponse>({
    queryKey: ["sqlSyncErrorSummary"],
    queryFn: fetchSqlSyncErrorSummary,
  });

  const {
    data: detailsData,
    isLoading: detailsLoading,
    isError: detailsError,
  } = useQuery<SqlSyncErrorDetailsResponse>({
    queryKey: ["sqlSyncErrorDetails", selectedError?.entityType, selectedError?.errorPattern],
    queryFn: () =>
      fetchSqlSyncErrorDetails(
        selectedError!.entityType,
        selectedError!.errorPattern,
        10
      ),
    enabled: !!selectedError,
  });

  const handleErrorClick = (entityType: string, errorGroup: SqlSyncErrorGroup) => {
    setSelectedError({
      entityType,
      errorPattern: errorGroup.errorPattern,
    });
  };

  return (
    <ViewGuard section="scheduler" fallback={
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8">
          <div className="mb-4">
            <span className="material-icons text-6xl text-gray-400">block</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to view SQL Sync Errors</p>
        </div>
      </div>
    }>
    <PageWrapper>
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-orange-500" />
            <h1 className="text-2xl font-bold">SQL Sync Errors</h1>
            {summaryData && summaryData.totalErrors > 0 && (
              <Badge variant="destructive" className="text-sm">
                {summaryData.totalErrors} total errors
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setLocation("/sql-sync")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to SQL Sync
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchSummary()}
              disabled={summaryFetching}
            >
              {summaryFetching ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              Refresh
            </Button>
          </div>
        </div>

        {/* Loading state */}
        {summaryLoading && (
          <div className="flex items-center gap-2 text-muted-foreground p-8">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading error summary...
          </div>
        )}

        {/* Error state */}
        {summaryError && (
          <div className="text-red-600 text-sm p-4 bg-red-50 rounded-lg">
            {(summaryErrorObj as Error)?.message || "Failed to load error summary"}
          </div>
        )}

        {/* No errors */}
        {!summaryLoading && !summaryError && summaryData?.totalErrors === 0 && (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              <div className="text-green-600 text-lg font-medium mb-2">No errors found</div>
              <p>All SQL Sync entities are running without errors.</p>
            </CardContent>
          </Card>
        )}

        {/* Error summary by entity */}
        {!summaryLoading && !summaryError && summaryData && summaryData.entities.length > 0 && (
          <div className="space-y-4">
            {summaryData.entities.map((entity) => (
              <Card key={entity.entityType}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {entity.entityType}
                      <Badge variant="destructive">{entity.totalFailed} errors</Badge>
                      {entity.totalDeadLetter > 0 && (
                        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                          {entity.totalDeadLetter} dead-letter
                        </Badge>
                      )}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50%]">Error Pattern</TableHead>
                        <TableHead className="w-[100px] text-right">Count</TableHead>
                        <TableHead className="w-[180px]">Last Occurred</TableHead>
                        <TableHead className="w-[80px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entity.errorGroups.map((group, idx) => (
                        <TableRow
                          key={idx}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleErrorClick(entity.entityType, group)}
                        >
                          <TableCell className="font-mono text-xs">
                            {truncateError(group.errorPattern)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {group.count}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(group.lastOccurred)}
                          </TableCell>
                          <TableCell>
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Error details dialog */}
        <Dialog open={!!selectedError} onOpenChange={() => setSelectedError(null)}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                Error Details: {selectedError?.entityType}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-muted p-3 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Error Pattern</div>
                <div className="font-mono text-sm break-all">
                  {selectedError?.errorPattern}
                </div>
              </div>

              {detailsLoading && (
                <div className="flex items-center gap-2 text-muted-foreground p-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading error details...
                </div>
              )}

              {detailsError && (
                <div className="text-red-600 text-sm">Failed to load error details</div>
              )}

              {!detailsLoading && !detailsError && detailsData && (
                <div className="space-y-3">
                  <div className="text-sm font-medium">
                    Sample Records ({detailsData.records.length})
                  </div>
                  {detailsData.records.map((record, idx) => (
                    <div key={idx} className="border rounded-lg p-3 bg-muted/30">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">EntityKey: {record.entityKey}</Badge>
                          <Badge variant="secondary">Attempts: {record.attemptCount}</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatDate(record.failedAt)}
                        </div>
                      </div>
                      <pre className="text-xs bg-white p-3 rounded border overflow-auto whitespace-pre-wrap">
                        {record.errorMessage}
                      </pre>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </PageWrapper>
    </ViewGuard>
  );
}
